import React, { useRef, useEffect, useState } from "react";
import {
  IconClock,
  IconChalkboard,
  IconWorld,
  IconCash,
  IconUsers,
  IconBook,
} from "@tabler/icons-react";

const WhyUs = () => {
  const sectionsRef = useRef([]);
  const [scrollState, setScrollState] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setScrollState((prev) => {
          const updatedState = [...prev];
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = sectionsRef.current.indexOf(entry.target);
              if (index !== -1) {
                updatedState[index] = true;
              }
            }
          });
          return updatedState;
        });
      },
      { threshold: 0.3 }
    );

    sectionsRef.current
      .filter((section) => section instanceof Element)
      .forEach((section) => observer.observe(section));

    return () => {
      sectionsRef.current
        .filter((section) => section instanceof Element)
        .forEach((section) => observer.unobserve(section));
      observer.disconnect();
    };
  }, []);

  const features = [
    {
      title: "Scheduled Learning",
      description: "Choose from predefined time slots that fit your routine.",
      miniSentence: "Structured options to balance your schedule.",
      icon: IconClock,
    },
    {
      title: "Expert Instructors",
      description: "Guided by professionals, learn the nuances of Punjabi language and culture.",
      miniSentence: "Our instructors bring years of teaching experience.",
      icon: IconChalkboard,
    },
    {
      title: "Cultural Immersion",
      description: "Experience Punjabi culture through real-world scenarios and traditions.",
      miniSentence: "Learn language while embracing authentic cultural values.",
      icon: IconWorld,
    },
    {
      title: "Affordable Pricing",
      description: "Accessible learning at prices suitable for all.",
      miniSentence: "High-quality courses without breaking the bank.",
      icon: IconCash,
    },
    {
      title: "Community Support",
      description: "Join a thriving community of learners and enthusiasts.",
      miniSentence: "Build connections and stay motivated with peers.",
      icon: IconUsers,
    },
    {
      title: "Comprehensive Resources",
      description: "Rich resources like videos, quizzes, and interactive sessions.",
      miniSentence: "Engage with a variety of tools to enhance your learning.",
      icon: IconBook,
    },
  ];

  return (
    <section id="why-us-randomized">
      <div className="randomized-container">
        <h2 className="randomized-title">Why Choose Us?</h2>
        <div className="randomized-grid">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`randomized-feature ${
                scrollState[index] ? "randomized-in-view" : ""
              }`}
              ref={(el) => (sectionsRef.current[index] = el)}
            >
              <div className="randomized-icon-wrapper">
                <feature.icon size={50} stroke={2} />
              </div>
              <div className="randomized-text">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <small>{feature.miniSentence}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        #why-us-randomized {
          padding: 60px 20px;
          background: linear-gradient(135deg, #f9f9f9, #e8e8e8);
        }

        .randomized-container {
          max-width: 1200px;
          margin: 0 auto;
          text-align: center;
        }

        .randomized-title {
          margin-bottom: 50px;
          font-size: 2.5rem;
          color: #333;
        }

        .randomized-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .randomized-feature {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 20px;
          border-radius: 8px;
          opacity: 0;
          transform: translateY(50px);
          transition: all 0.8s ease-in-out, box-shadow 0.3s ease;
        }

        .randomized-feature.randomized-in-view {
          opacity: 1;
          transform: translateY(0);
        }

        .randomized-feature:hover {
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.15);
        }

        .randomized-icon-wrapper {
          width: 80px;
          height: 80px;
          border: 2px solid black;
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 15px;
          transition: border-color 0.3s ease, background 0.3s ease;
        }

        .randomized-icon-wrapper:hover {
          border-color: purple;
          background: #f0f0f0;
        }

        .randomized-feature .randomized-text {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease-in-out, transform 0.8s ease-in-out;
        }

        .randomized-feature.randomized-in-view .randomized-text {
          opacity: 1;
          transform: translateY(0);
        }

        .randomized-feature .randomized-text h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: #333;
        }

        .randomized-feature .randomized-text p {
          font-size: 1rem;
          color: #666;
          line-height: 1.5;
        }

        .randomized-feature .randomized-text small {
          display: block;
          margin-top: 10px;
          font-size: 0.9rem;
          color: #888;
        }

        @media (max-width: 1100px) {
          .randomized-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .randomized-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default WhyUs;
